import { createContext, useContext, useState, useEffect, useCallback } from "react";
import authApi from "../api/authApi";
import { settingsApi } from "../api/settingsApi";
import subscriptionApi from "../api/subscriptionApi";
import NAV from "../theme/navConfig";

const AuthContext = createContext(null);

const SESSION_KEY = "garment_session";     // stores full user data including permissions
const TOKEN_KEY = "garment_token";       // stores JWT token

// ─── Helpers ────────────────────────────────────────────────────────────────
const MANAGE_ROLES = ["SUPER_ADMIN", "COMPANY_OWNER", "COMPANY_ADMIN"];

// Company name is shown exactly as stored on the company record.
function displayCompanyName(raw) {
  return raw;
}

function parseLoginResponse(d) {
  return {
    id: d.userId,
    fullName: d.fullName,
    username: d.username,
    email: d.email,
    role: d.role,
    companyId: d.companyId,
    companyName: displayCompanyName(d.companyName),
    permissions: d.permissions || {},
    accessibleFirms: d.accessibleFirms || [],
    accessibleLocations: d.accessibleLocations || [],
  };
}

// ─── Provider ────────────────────────────────────────────────────────────────
export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(SESSION_KEY));
      // Phase 36da.99.bp — re-apply the company display override on
      // restored sessions too, so users who were already logged in
      // before this change see the new name without re-logging in.
      if (cached && cached.companyName) cached.companyName = displayCompanyName(cached.companyName);
      return cached;
    } catch (_) { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companySettings, setCompanySettings] = useState(() => {
    try { return JSON.parse(localStorage.getItem('garment_settings')); } catch (_) { return null; }
  });

  // ── Subscription (global software licence) ──────────────────────────────
  const [subscription, setSubscription] = useState(null);

  const refreshSubscription = useCallback(async () => {
    try {
      const res = await subscriptionApi.status();
      const s = res?.data?.data ?? res?.data;
      if (s) setSubscription(s);
      return s;
    } catch (_) { return null; }
  }, []);

  // A 402 anywhere flips us to LOCKED immediately (then we re-confirm via API).
  useEffect(() => {
    const onLocked = () => {
      setSubscription(prev => ({ ...(prev || {}), status: "LOCKED", locked: true }));
      refreshSubscription();
    };
    window.addEventListener("subscription-locked", onLocked);
    return () => window.removeEventListener("subscription-locked", onLocked);
  }, [refreshSubscription]);

  // ── On mount: verify token is still valid ──────────────────────────────
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    authApi.me()
      .then(res => {
        const meData = res.data?.data ?? res.data;
        // Merge: keep permissions/firms from stored session, refresh basic profile from me()
        setUser(prev => ({ ...(prev || {}), ...meData }));
        // Subscription gate — fetch global licence status on boot
        refreshSubscription();
        // Also refresh company settings
        settingsApi.get().then(sr => {
          const s = sr?.data?.data ?? sr?.data;
          if (s) { setCompanySettings(s); localStorage.setItem('garment_settings', JSON.stringify(s)); }
        }).catch(() => { });
      })
      .catch(() => {
        // Token invalid — clear everything
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(SESSION_KEY);
        setToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Login ─────────────────────────────────────────────────────────────
  const login = useCallback(async (loginId, password) => {
    setError(null);
    try {
      const res = await authApi.login(loginId, password);
      const d = res.data?.data ?? res.data;
      const newToken = d.token;
      const userData = parseLoginResponse(d);

      localStorage.setItem(TOKEN_KEY, newToken);
      localStorage.setItem(SESSION_KEY, JSON.stringify(userData));
      setToken(newToken);
      setUser(userData);
      // Subscription gate — load global licence status right after login
      refreshSubscription();
      // Fetch company settings (VL%, OS%, etc)
      try {
        const sr = await settingsApi.get();
        const s = sr?.data?.data ?? sr?.data;
        if (s) { setCompanySettings(s); localStorage.setItem('garment_settings', JSON.stringify(s)); }
      } catch (_) { }
      return userData;
    } catch (err) {
      // Extract a user-friendly message from the failed response.
      // Backend shape: { success: false, message: "Invalid username or password" }
      // Axios wraps the body in err.response.data — handle every shape gracefully.
      let msg = "Login failed. Please try again.";
      const body = err?.response?.data;
      if (typeof body === "string" && body.trim()) {
        msg = body;
      } else if (body?.message) {
        msg = body.message;
      } else if (err?.message && !/Network Error/i.test(err.message)) {
        msg = err.message;
      } else if (err?.message && /Network Error/i.test(err.message)) {
        msg = "Unable to reach the server. Check your connection and try again.";
      }
      // Map specific status codes to friendlier copy where the generic backend
      // message isn't helpful enough.
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // 403 on login = bad credentials (backend throws custom AccessDeniedException)
        // 401 on login = BadCredentialsException from Spring
        if (!body?.message || /access|forbidden|denied/i.test(msg)) {
          msg = "Invalid username or password.";
        }
      } else if (status === 404) {
        msg = "Invalid username or password.";
      }
      setError(msg);
      throw err;                       // let Login.jsx catch too, but state already set
    }
  }, []);

  // ── Logout ────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch (_) { }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem('garment_settings');
    setToken(null);
    setUser(null);
    setCompanySettings(null);
  }, []);

  // ── Permission helpers ────────────────────────────────────────────────
  /** True if the user can create / edit / delete master data. */
  const canManage = useCallback(() => {
    if (!user) return false;
    return MANAGE_ROLES.includes(user.role);
  }, [user]);

  /**
   * hasPermission("PURCHASE_ORDER", "CREATE") → boolean
   *
   * Permissions map on the wire (from PermissionService) looks like:
   *   { "PURCHASE_ORDER": ["VIEW", "CREATE", "EDIT"], "GRN": ["VIEW"] }
   *
   * Super Admin and Company Owner bypass every check.
   */
  const hasPermission = useCallback((pageModule, accessType) => {
    if (!user) return false;
    if (["SUPER_ADMIN", "COMPANY_OWNER"].includes(user.role)) return true;
    if (!pageModule) return true;                       // un-moduled nav items stay visible
    const actions = user.permissions?.[pageModule];
    if (!actions) return false;
    if (Array.isArray(actions)) return actions.includes(accessType);
    // Defensive: if the server ever serializes as Set/object, support both.
    if (actions instanceof Set) return actions.has(accessType);
    if (typeof actions === "object") return !!actions[accessType];
    return false;
  }, [user]);

  /** Convenience — VIEW access check used by Sidebar filtering + route guards. */
  const canView = useCallback((pageModule) => {
    return hasPermission(pageModule, "VIEW");
  }, [hasPermission]);

  /**
   * Scan navConfig top-down and return the id of the first page the user
   * has VIEW permission on.  Used to redirect users who:
   *   - Log in but lack DASHBOARD:VIEW
   *   - Type a URL for a page they don't have access to
   *
   * The scan respects sidebar order so users land on the most prominent
   * allowed page (typically from the Masters or Production group for
   * non-admin roles).  Items without a `module` are treated as always-allowed
   * (utility pages) — we skip those to avoid sending everyone to
   * an admin-only utility page.
   *
   * Returns "dashboard" as last resort — if the user genuinely has no
   * permissions at all, the dashboard renders a friendly empty state.
   */
  const getFirstAllowedPage = useCallback(() => {
    for (const group of NAV) {
      for (const item of group.items || []) {
        if (!item.module) continue;            // skip un-moduled items
        if (canView(item.module)) return item.id;
      }
    }
    return "dashboard";                        // ultimate fallback
  }, [canView]);

  const value = {
    user, token, loading, error,
    setError, login, logout,
    isAuthenticated: !!token && !!user,
    canManage, hasPermission, canView, getFirstAllowedPage,
    companySettings, setCompanySettings,
    subscription, refreshSubscription,
    // Convenience: default VL% and OS% from company settings
    defaultVl: companySettings?.defaultVlPercent ?? 0,
    defaultOs: companySettings?.defaultOsPercent ?? 0,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}